import React from 'react';
import {connect} from 'react-redux';
import {Grid, Divider, InputAdornment} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {Dispatch} from 'spectra-logic-ui';
import {createPatch, patchCollection} from 'spectra-logic-ui/actions';
import {Color} from 'spectra-logic-ui/colors';
import {Checkbox, FormDialog, FormRenderProps, SingleSelect, TextField} from 'spectra-logic-ui/components';

import {isLocal} from '@/local';
import {LifecycleV2} from '@/types';
import DialogDescription from '@/components/form/dialog_description';
import {System, SystemPatchFields} from '@/system/types';

type Props = {
  system: System;
  lifecycles: LifecycleV2[];
  onSubmit: (values: FormValues) => Promise<any>;
}

export type FormValues = {
  monitor: boolean;
  nightly: string;
  namespace?: string;
  awsRegion?: string;
  awsKey?: string;
  awsSecret?: string;
}

const useStyles = makeStyles({
  divider: {
    marginTop: '20px',
    marginBottom: '20px',
  },
  grid: {
    marginTop: '5px',
  },
  warning: {
    color: Color.RED,
  },
});

const monitorTooltip = 'Automatically send diagnostic information to Spectra Logic.';
const nightlyTooltip = 'The time when all systems perform nightly processing. '+
    'Changing this value will not change actions already scheduled. '+
    'All endpoints will restart to recognize the new value.';

const EditSettingsDialog = ({system, lifecycles, ...otherProps}: Props) => {
  const initialValues = {
    monitor: system.monitor,
    nightly: system.nightly,
    defaultLifecycleId: system.defaultLifecycleId,
    namespace: system.namespace,
    awsRegion: system.awsRegion,
  } as FormValues;

  const classes = useStyles();

  return (
    <FormDialog title='Edit Global Settings' initialValues={initialValues} {...otherProps}>
      {({values}: FormRenderProps) => (
        <>
          <Checkbox name='monitor' label='Enable Diagnostic Monitor' tooltip={monitorTooltip} />
          <TextField name='nightly' label='Nightly Processing'
            InputProps={{endAdornment: <InputAdornment position='start'>UTC</InputAdornment>}}
            tooltip={nightlyTooltip}/>
          {initialValues.nightly != values.nightly &&
            <p className={classes.warning}>Warning: Changing the nightly processing
              time will immediately restart all endpoints.</p>}
          <Grid item xs={12}>
            <SingleSelect
              name='defaultLifecycleId' label='Default Lifecycle'
              options={lifecycles.map((l) => ({key: l.id, text: l.name}))}
            />
          </Grid>
          {isLocal() && <>
            <Divider className={classes.divider} />
            <DialogDescription>Configure AWS Infrastructure</DialogDescription>
            <Grid container spacing={2} className={classes.grid}>
              <Grid item xs={12}><TextField name='namespace' label='Namespace' /></Grid>
              <Grid item xs={12}><TextField name='awsRegion' label='AWS Region' /></Grid>
              <Grid item xs={12}><TextField name='awsKey' label='AWS Access Key ID' /></Grid>
              <Grid item xs={12}><TextField name='awsSecret' label='AWS Secret Access Key' /></Grid>
            </Grid>
          </>}
        </>
      )}
    </FormDialog>
  );
};

const mapDispatchToProps = (dispatch: Dispatch, {system}: Props) => ({
  onSubmit: (values: FormValues) => {
    const upd = createPatch(SystemPatchFields, system, values);

    return dispatch(patchCollection('system', upd));
  },
});

export default connect(undefined, mapDispatchToProps)(EditSettingsDialog);
